import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { PriceGroupsApi } from "apis/PriceGroupsApi";
import { PriceGroupsPriceApi } from "apis/PriceGroupsPriceApi";
import { ApplyAndSyncEmpty } from "models/common/applyAndSync";
import { PriceGroup } from "models/dbModels/priceGroups/PriceGroup";
import { PriceGroupsPriceAddOrUpdate, PriceGroupsPriceView } from "models/dbModels/priceGroups/PriceGroupsPrice";
import { PriceGroupsSearchFilterQuery } from "models/filters/priceGroups/PriceGroupsSearchFilterQuery";
import { PriceGroupsPricingSearchFilterQuery } from "models/filters/priceGroupsPricing/PriceGroupsPricingSearchFilterQuery";

export const getPriceGroups = createAsyncThunk("priceGroups/getPriceGroups", async (params?: PriceGroupsSearchFilterQuery) => {
    const response = await PriceGroupsApi.Search(params ?? {});
    return response.data;
});

export const getPriceGroupDetails = createAsyncThunk("priceGroups/getPriceGroupDetails", async (priceGroupId: number) => {
    const response = await PriceGroupsApi.Search({ ids: [priceGroupId] });
    return response.data;
});

export const createPriceGroup = createAsyncThunk("priceGroups/createPriceGroup", async (params: PriceGroup) => {
    const response = await PriceGroupsApi.Add([params]);
    return response.data;
});

export const updatePriceGroup = createAsyncThunk("priceGroups/updatePriceGroup", async (params: PriceGroup) => {
    const response = await PriceGroupsApi.Update([params]);
    return response.data;
});

export const deletePriceGroups = createAsyncThunk("priceGroups/deletePriceGroup", async (priceGroupsIds: number[]) => {
    const response = await PriceGroupsApi.Delete({ ids: priceGroupsIds });
    return response.data;
});

export const getPriceGroupsPrice = createAsyncThunk("priceGroups/getPriceGroupsPrice", async (params: PriceGroupsPricingSearchFilterQuery) => {
    params = { ...params, limit: params.limit || 100, offset: params.offset || 0 };
    const response = await PriceGroupsPriceApi.Search(params);
    return response.data;
});

export const modifyPriceGroupsPrice = createAsyncThunk("priceGroups/modifyPriceGroupsPrice", async (params: PriceGroupsPriceAddOrUpdate[]) => {
    const response = await PriceGroupsPriceApi.Modify(params);
    return response.data;
});

export const applyPriceGroupsPrice = createAsyncThunk("priceGroups/applyPriceGroupsPrice", async (params: ApplyAndSyncEmpty<PriceGroupsPricingSearchFilterQuery>) => {
    const response = await PriceGroupsPriceApi.Apply(params);
    return response.data;
});

type IntiialStateType = {
    id: number;
    name: string;
    stock: PriceGroupsPriceView[];
    selectedRows: { position: number, id: number }[][];
    deselectedRows: { position: number, id: number }[][];
    loading: boolean;
    loadingStocks: boolean;
    keyword: string;
    totalRows: number;
    totalPages: number;
    pageIndex: number;
    pageSize: number;
    error: string;
    table: PriceGroup[];
};

const initialState: IntiialStateType = {
    id: 0,
    name: "",
    stock: [],
    selectedRows: [],
    deselectedRows: [],
    loading: false,
    loadingStocks: false,
    keyword: "",
    totalRows: 0,
    totalPages: 0,
    pageIndex: 0,
    pageSize: 100,
    error: "",
    table: [],
};

type FilterType = {
    departmentIdFilter: number;
    subdepartmentIdFilter: number;
    supplierIdFilter: number;
    manufacturerIdFilter: number;
    stockGroupIdFilter: number;
    activeFilter: number;
    ethicalFilter: number;
    scheduleFilter: string;
    pbsFilter: number;
};

const filter: FilterType = {
    departmentIdFilter: 0,
    subdepartmentIdFilter: 0,
    supplierIdFilter: 0,
    manufacturerIdFilter: 0,
    stockGroupIdFilter: 0,
    activeFilter: 0,
    ethicalFilter: 0,
    scheduleFilter: "0",
    pbsFilter: 0,
}

const priceGroupSlice = createSlice({
    name: "priceGroupsSlice",
    initialState: {
        ...initialState,
        ...filter,
    },
    reducers: {
        cleanPriceGroups: (state) => {
            return {
                ...state,
                ...initialState,
            };
        },
        updatePriceGroupField: (state, action) => {
            return {
                ...state,
                [action.payload.key]: action.payload.value,
            };
        },
        clearFilters: (state) => {
            return {
                ...state,
                ...filter,
            };
        },
        addSelectedRows: (state, action) => {
            // @ts-ignore
            state.selectedRows[action.payload.index] = action.payload.rows;
        },
        addDeselectedRows: (state, action) => {
            // @ts-ignore
            state.deselectedRows[action.payload.index] = action.payload.rows;
        },
        updatePriceGroupStock: (state, action) => {
            // @ts-ignore
            state.stock[action.payload.index][`${action.payload.key}`] = action.payload.value;
        },
        addAGGridSelectedRows: (state, action) => {
            return {
                ...state,
                selectedRows: action.payload
            };
        },
        addAGGrideDeselectedRows: (state, action) => {
            return {
                ...state,
                deselectedRows: action.payload
            };
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getPriceGroups.pending, (state) => {
            state.loading = true;
            state.table = [];
        });
        builder.addCase(getPriceGroups.fulfilled, (state, action) => {
            state.loading = false;
            state.table = action.payload.data ? action.payload.data : [];
        });
        builder.addCase(getPriceGroups.rejected, (state, action) => {
            state.loading = false;
            state.table = [];
        });
        builder.addCase(getPriceGroupDetails.fulfilled, (state, action) => {
            state.loading = false;
            state.name = action.payload.data[0].name;
            state.id = action.payload.data[0].id;
        });
        builder.addCase(getPriceGroupsPrice.pending, (state, action) => {
            state.loadingStocks = true;
            state.stock = [];
        });
        builder.addCase(getPriceGroupsPrice.fulfilled, (state, action) => {
            state.loadingStocks = false;
            let stock = [];
            stock = action.payload.data ? action.payload.data.sort((a: any, b: any) => a.managedStockName.localeCompare(b.managedStockName)) : [];

            stock = stock.map((item: any) => {
                return { ...item, id: item.managedStockId }
            });

            state.stock = stock;
            state.totalRows = action.payload.totalRows;
            state.totalPages = Math.ceil(Math.ceil(action.payload.totalRows) / state.pageSize);
        });
    },
});

export const {
    clearFilters,
    cleanPriceGroups,
    addSelectedRows,
    addDeselectedRows,
    updatePriceGroupField,
    updatePriceGroupStock,
    addAGGridSelectedRows,
    addAGGrideDeselectedRows
} = priceGroupSlice.actions;

export default priceGroupSlice.reducer;
